import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import axios from 'axios';
import styles from '../assets/VerifyUser.module.css';
import { Phone, phoneActions } from '../store/phone';
import { verifyPhoneNumberPayload } from '../helper/payloadHelper';

interface VerifyUserProps {
  rsvpCode: string,
  token: string,
  siteName: string
}

const {
  ERROR_URL,
  CREATE_PASSWORD_URL,
  VERIFY_RSVP_CODE,
  CONFIRM_MOBILE_URL,
  VERIFYUSERPHONENUMBER_URL
} = require('../helper/application-conf').default;

const {
  Input, ButtonBase, dlsBrightBlue, ButtonPrimary, Alert, DialogBox, DialogBoxHeader, DialogBoxBody,
} = require('@americanexpress/dls-react');

const VerifyUser = (props: VerifyUserProps) => {
  const { t } = useTranslation();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();

  const phone: Phone = useAppSelector((state) => state.phone);
  const [loading, setLoading] = useState(true);
  const [notFamiliarModalOpen, setNotFamiliarModalOpen] = useState(false);

  const [userName, setUserName] = useState('');
  const [userId, setUserId] = useState('');
  const [site, setSite] = useState('');
  const [isPasswordCreated, setIsPasswordCreated] = useState(false);
  const [error, setError] = useState(false);
  const [error0, setError0] = useState(false);
  const [error1, setError1] = useState(false);
  const [error2, setError2] = useState(false);
  const [isVerifyPhoneDisabled, setIsVerifyPhoneDisabled] = useState(false);
  const { ProgressCircle } = require('@americanexpress/dls-react');
  const [fourDigit, setFourDigit] = useState('');
  const [createdByName, setCreatedByName] = useState('');

  const inputRef1 = React.useRef<HTMLInputElement>(null);
  const inputRef2 = React.useRef<HTMLInputElement>(null);
  const inputRef3 = React.useRef<HTMLInputElement>(null);
  const inputRef4 = React.useRef<HTMLInputElement>(null);
  const inputRef5 = React.useRef<HTMLInputElement>(null);
  const inputRef6 = React.useRef<HTMLInputElement>(null);

  const {
    siteName,
    token,
    rsvpCode
  } = props;

  useEffect(() => {
    verifyRsvpCode();
    inputRef1.current?.focus();
  }, [appDispatch]);

  const verifyRsvpCode = async () => {
    const SITE_NAME = window.location.pathname.split('/')[2];
    await axios.get(
      `${VERIFY_RSVP_CODE}/${SITE_NAME}/${rsvpCode}`
    ).then((getResponse) => {
      if (getResponse.status == 200 && getResponse.data) {
        const data = getResponse.data.body.data;
        const usrId = data.userId;
        const usrName = data.userName;
        const isPasswordCreated = data.isPasswordCreated;

        setUserName(usrName);
        setUserId(usrId);
        setSite(SITE_NAME);
        setIsPasswordCreated(isPasswordCreated);
        setCreatedByName(data.createdByName);

        if (data.expired) // Expired screen
        {
          const errorText = { message: "", mobileVerificationStatus: "" };
          navigate(ERROR_URL, { state: { errorText } });
        }
        else if (!data.success) //Display Mobile Verification Screeen
        {
          setLoading(false);
          getPhoneNumber(data.userId);
        }
        else if (data.success && !data.isPasswordCreated) //Display Change Password Screeen
        {
          const data = { userId: usrId, userName: usrName, site: SITE_NAME, token: token, isPhoneVerified: true };
          navigate(CREATE_PASSWORD_URL, { state: { data } });
        }
        else if (data.success && data.isPasswordCreated) //Display One AP Login Screeen
        {
          window.location.pathname = `/${SITE_NAME}`;
        }

        setLoading(false);
      }
      else {
        navigate(ERROR_URL);
      }
    }, (error) => {
      setLoading(false);
      if (error.response && error.response.status === 400 && error.response.data) {
        const errorText = { message: error.response.data, mobileVerificationStatus: "" };
        navigate(ERROR_URL, { state: { errorText } });
      } else {
        navigate(ERROR_URL);
      }
    });
  };

  const getPhoneNumber = async (userId: string) => {
    const SITE_NAME = window.location.pathname.split('/')[2];
    await axios.get(
      `${CONFIRM_MOBILE_URL}/${SITE_NAME}/${userId}`
    ).then((response: any) => {
      if (response.status === 200 && response.data.statusCode === "OK") {
        let lastFourDigit = response.data.body;
        if(lastFourDigit.toString().length < 4) {
           lastFourDigit = lastFourDigit.toString().padStart(4,"0");
        }
        setFourDigit(lastFourDigit);
        const splitLastFourDigit = lastFourDigit.toString().split('');
        const fourDigit = splitLastFourDigit.map(Number);
        setLastFourDigit(fourDigit);
      }
      else {
        navigate(ERROR_URL);
      }
    })
  };

  const setLastFourDigit = useCallback((value: any) => {
    appDispatch(phoneActions.setPhone({
      ...phone,
      key7: value[0],
      key8: value[1],
      key9: value[2],
      key10: value[3]
    }));
  }, [phone, appDispatch]);

  const updateField = useCallback((field: string, value: string, e: any = undefined) => {
    if(value.length>1){
      return ;
    }
    if (e && e.target.getAttribute('numeric') === 'true' && e.target.value.match(/\D/)) {
      return;
    }

    if (value) {
      if (field === 'key1') {
        inputRef2.current?.focus();
        inputRef2.current?.select();
      } else if (field === 'key2') {
        inputRef3.current?.focus();
        inputRef3.current?.select();
      } else if (field === 'key3') {
        inputRef4.current?.focus();
        inputRef4.current?.select();
      } else if (field === 'key4') {
        inputRef5.current?.focus();
        inputRef5.current?.select();
      } else if (field === 'key5') {
        inputRef6.current?.focus();
        inputRef6.current?.select();
      }
    }

    appDispatch(phoneActions.setPhone({
      ...phone,
      [field]: value,
    }));
  }, [phone, appDispatch]);

  const handleBackspaceAndEnter = useCallback((e: any = undefined,value: string, field: string, index: number) => {
    if (e.key === "Backspace" && !value && index > 0) {
      if (field === 'key6' ) {
        inputRef5.current?.focus();
        inputRef5.current?.select();
      } else if (field === 'key5') {
        inputRef4.current?.focus();
        inputRef4.current?.select();
      } else if (field === 'key4') {
        inputRef3.current?.focus();
        inputRef3.current?.select();
      } else if (field === 'key3') {
        inputRef2.current?.focus();
        inputRef2.current?.select();
      } else if (field === 'key2') {
        inputRef1.current?.focus();
        inputRef1.current?.select();
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      if (field === 'key6' ) {
        inputRef5.current?.focus();
        inputRef5.current?.select();
      } else if (field === 'key5') {
        inputRef4.current?.focus();
        inputRef4.current?.select();
      } else if (field === 'key4') {
        inputRef3.current?.focus();
        inputRef3.current?.select();
      } else if (field === 'key3') {
        inputRef2.current?.focus();
        inputRef2.current?.select();
      } else if (field === 'key2') {
        inputRef1.current?.focus();
        inputRef1.current?.select();
      }
    }

    if (e.key === "ArrowRight" && index < 7) {
      if (field === 'key1') {
        inputRef2.current?.focus();
        inputRef2.current?.select();
      } else if (field === 'key2') {
        inputRef3.current?.focus();
        inputRef3.current?.select();
      } else if (field === 'key3') {
        inputRef4.current?.focus();
        inputRef4.current?.select();
      } else if (field === 'key4') {
        inputRef5.current?.focus();
        inputRef5.current?.select();
      } else if (field === 'key5') {
        inputRef6.current?.focus();
        inputRef6.current?.select();
      }
    }

    if (e.key === "Enter" && value && index < 7) {
      if (field === 'key1') {
        inputRef2.current?.focus();
        inputRef2.current?.select();
      } else if (field === 'key2') {
        inputRef3.current?.focus();
        inputRef3.current?.select();
      } else if (field === 'key3') {
        inputRef4.current?.focus();
        inputRef4.current?.select();
      } else if (field === 'key4') {
        inputRef5.current?.focus();
        inputRef5.current?.select();
      } else if (field === 'key5') {
        inputRef6.current?.focus();
        inputRef6.current?.select();
      }
    }

  }, []);

  const verifyPhoneNumber = useCallback(async (phoneNo: any) => {
    setError(false);
    setError0(false);
    setError1(false);
    setError2(false);

    if(phoneNo.payload.length < 10 || phoneNo.payload === ''){
      setError(true);
    }else{
    const verifyPayload = verifyPhoneNumberPayload(userName, phoneNo.payload)
    await axios.put(
      `${VERIFYUSERPHONENUMBER_URL}/${site}`, verifyPayload
    ).then((getResponse) => {
      if (getResponse.status === 200 && getResponse.data) {
        const res = getResponse.data.body.data;
        const data = { userId: userId, userName: userName, site: site, token: token, isPhoneVerified: true };
        if (res.success) {
          if (!isPasswordCreated) {
            navigate(CREATE_PASSWORD_URL, { state: { data } });
          } else {
            window.location.pathname = `/${site}`;
          }
        } else {
          if (res.numberOfAttemptsLeft === 0) {
            setIsVerifyPhoneDisabled(true);
            setError0(true);
          }
          else if (res.numberOfAttemptsLeft === 1) {
            setError1(true);
          }
          else if (res.numberOfAttemptsLeft === 2) {
            setError2(true);
          }
        }
      }
      else {
        navigate(ERROR_URL);
      }
    }, (error) => {
      if (error.response && error.response.status === 400 && error.response.data) {
        const errorText = { message: error.response.data, mobileVerificationStatus: "" };
        navigate(ERROR_URL, { state: { errorText } });
      } else {
        navigate(ERROR_URL);
      }
    }
  );
  }
 }, [siteName, userName, '']);

  const handleFamiliarPhoneNumber = useCallback(() => {
    setNotFamiliarModalOpen(true);
  }, []);

  const close = useCallback(() => {
    setNotFamiliarModalOpen(false)
  }, []);

  return (
    <div className="container">
      {loading ? (
        <div className="row text-align-center pad-4-t flex flex-justify-center">
          <ProgressCircle size="lg" />
        </div>
      ) : (
        <div>
          <div className="row pad-4-t">
            <div className="col-md-offset-4 col-md-4">
              <h5 className="heading-5 text-align-center">{t('verify_user.header')}</h5>
            </div>
          </div>
          <div className="row pad-3-t">
            <div className="col-md-offset-4 col-md-4">
              <div className="body-3 text-align-center"><span>{t('verify_user.description')}</span>{fourDigit}<span>{t('verify_user.description2')}</span></div>
            </div>
          </div>
          <div className="row pad-3-t">
            <div className="col-md-offset-3 col-md-4">
              <div className="flex flex-align-center wrapper inputverify">
                <Input type='number' ref={inputRef1} id="entity-name-input-key1" aria-label="input-key1" onKeyUp={(e: any) => handleBackspaceAndEnter(e, e.target.value, 'key1', 1)} maxLength={1} numeric="true" value={phone.key1} required={true} className={`${styles.inputverify}`} onChange={(e: any) => updateField('key1', e.target.value, e)} />
                <Input type='number'  ref={inputRef2} id="entity-name-input-key2" aria-label="input-key2" onKeyUp={(e: any) => handleBackspaceAndEnter(e, e.target.value, 'key2', 2)} maxLength={1} numeric="true" value={phone.key2} required={true} className={`${styles.inputverify}`} onChange={(e: any) => updateField('key2', e.target.value, e)} />
                <Input type='number'  ref={inputRef3} id="entity-name-input-key3" aria-label="input-key3" onKeyUp={(e: any) => handleBackspaceAndEnter(e,e.target.value, 'key3', 3)} maxLength={1} numeric="true" value={phone.key3} required={true} className={`${styles.inputverify}`} onChange={(e: any) => updateField('key3', e.target.value, e)} />
                <div className="dashsymbol margin-1-r">-</div>
                <Input type='number'  ref={inputRef4} id="entity-name-input-key4" aria-label="input-key4" onKeyUp={(e: any) => handleBackspaceAndEnter(e,e.target.value, 'key4', 4)} maxLength={1} numeric="true" value={phone.key4} required={true} className={`${styles.inputverify}`} onChange={(e: any) => updateField('key4', e.target.value, e)} />
                <Input type='number'  ref={inputRef5} id="entity-name-input-key5" aria-label="input-key5" onKeyUp={(e: any) => handleBackspaceAndEnter(e,e.target.value, 'key5', 5)} maxLength={1} numeric="true" value={phone.key5} required={true} className={`${styles.inputverify}`} onChange={(e: any) => updateField('key5', e.target.value, e)} />
                <Input type='number'  ref={inputRef6} id="entity-name-input-key6" aria-label="input-key6" onKeyUp={(e: any) => handleBackspaceAndEnter(e,e.target.value, 'key6', 6)} maxLength={1} numeric="true" value={phone.key6} required={true} className={`${styles.inputverify}`} onChange={(e: any) => updateField('key6', e.target.value, e)} />
                <div className="dashsymbol margin-1-r">-</div>
                <Input id="entity-name-input-key7" maxLength={1} aria-label="input-key7" numeric="true" value={phone.key7} required={true} className={`${styles.inputverify}`} disabled={true} onChange={(e: any) => updateField('key7', e.target.value, e)} />
                <Input id="entity-name-input-key8" maxLength={1} aria-label="input-key8" numeric="true" value={phone.key8} required={true} className={`${styles.inputverify}`} disabled={true} onChange={(e: any) => updateField('key8', e.target.value, e)} />
                <Input id="entity-name-input-key9" maxLength={1} aria-label="input-key9" numeric="true" value={phone.key9} required={true} className={`${styles.inputverify}`} disabled={true} onChange={(e: any) => updateField('key9', e.target.value, e)} />
                <Input id="entity-name-input-key10" maxLength={1} aria-label="input-key10" numeric="true" value={phone.key10} required={true} className={`${styles.inputverify}`} disabled={true} onChange={(e: any) => updateField('key10', e.target.value, e)} />
              </div>
            </div>

            {error
              && (
                <div className="col-md-offset-4 col-md-4 flex-align-items-center">
                  <Alert className="pad-1-t flex">
                    <span className="body-1"> {t('verify_user.pleaseEnter6Digit')}</span>
                  </Alert>
                </div>
              )}
            {error0
              && (
                <div className="col-md-offset-3 col-md-6  flex-align-items-center">
                  <Alert className="pad-1-t flex">
                    <span className="body-1"> {t('verify_user.invaliderror')}</span>
                    <span className="body-2"> {t('verify_user.error0')}</span>
                    <span className="body-1"> {t('verify_user.pleasecontactyouradmin' , { createdByName: createdByName })} </span>
                  </Alert>
                </div>
              )}
            {error1
              && (
                <div className="col-md-offset-4 col-md-4 flex-align-items-center">
                  <Alert className="pad-1-t flex">
                    <span className="body-1"> {t('verify_user.invaliderror')}</span>
                    <span className="body-2"> {t('verify_user.error1')}</span>
                  </Alert>
                </div>
              )}
            {error2
              && (
                <div className="col-md-offset-4 col-md-4 flex-align-items-center">
                  <Alert className="pad-1-t flex">
                    <span className="body-1"> {t('verify_user.invaliderror')}</span>
                    <span className="body-2"> {t('verify_user.error2')}</span>
                  </Alert>
                </div>
              )}

          </div>
          <div className="row pad-3-t">
            <div className="col-md-offset-4 col-md-4">
              <div className="body-3 text-align-center">
                <ButtonPrimary className="text-align-center" size="md" onClick={() => { verifyPhoneNumber(phone) }} id="verify-phone-button" disabled={isVerifyPhoneDisabled}>{t('verify_user.verify')}</ButtonPrimary>
              </div>
            </div>
          </div>
          <div className="row pad-1-t">
            <div className="col-md-offset-4 col-md-4">
              <div className="body-3 text-align-center">
                <ButtonBase theme={dlsBrightBlue} onClick={handleFamiliarPhoneNumber}>
                  <span>{t('verify_user.notafamiliarphoneno')}</span>
                </ButtonBase>
              </div>
            </div>
          </div>
        </div>
      )}
      {notFamiliarModalOpen
        && (
          <DialogBox onClose={() => setNotFamiliarModalOpen(false)} id="copy-link-modal">
            <DialogBoxHeader id="copy-link-modal-header">
              <h3 className="heading-3">{t('verify_user.notafamiliarphoneno')}</h3>
            </DialogBoxHeader>
            <DialogBoxBody className={`${styles.dialogBodyStyle}`} id="copy-link-modal-body">
              <p className="pad-2-b">{t('verify_user.pleasecontactyouradmin' , { createdByName: createdByName })}</p>
              <ButtonPrimary className={`margin-auto-lr margin-1-b ${styles.btnStyle}`} size="xl" onClick={close} id="close-button" >{t('Close')}</ButtonPrimary>            
              </DialogBoxBody>
          </DialogBox>
        )}
    </div>
  );
};

export default VerifyUser;
